'use client';

import { useState, useEffect, useRef, FormEvent } from 'react';
import Icon from '@/components/ui/AppIcon';
import { useSiteLanguage } from '@/components/common/SiteLanguageProvider';

interface FormData {
  name: string;
  email: string;
  company: string;
  projectType: string;
  budget: string;
  projectGoals: string;
  keyFeatures: string;
  timeline: string;
  challenges: string;
  additionalNotes: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  projectGoals?: string;
  keyFeatures?: string;
  attachment?: string;
}

const TOTAL_STEPS = 5;

export default function ContactSection() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [step, setStep] = useState(1);
  const cardRef = useRef<HTMLDivElement>(null);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    company: '',
    projectType: '',
    budget: '',
    projectGoals: '',
    keyFeatures: '',
    timeline: '',
    challenges: '',
    additionalNotes: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const { language } = useSiteLanguage();

  const copy = {
    en: {
      title: "Let's Digitize What Matters Most",
      subtitle:
        "Tell us what you want to digitize - an idea, a process, a service, or your business operations - and we'll help you turn it into a practical digital solution.",
      responseTime: 'Response Time',
      responseTimeMessage: 'We typically respond within 24 hours on business days',
      wizard: {
        step1Title: 'Tell us how to reach you',
        step1Subtitle: '',
        step2Title: 'What area should we digitize first?',
        step2Subtitle: 'Choose the option that matches your current priority.',
        step3Title: 'What budget range works for you?',
        step3Subtitle: 'This helps us shape a practical low-cost approach.',
        step4Title: 'Great, now tell us the essentials',
        step4Subtitle: 'Share your goals and key requirements.',
        step5Title: 'Final details',
        step5Subtitle: 'Timeline, context, optional files, then we can start.',
        reviewTitle: 'Review',
        privacyNote: 'Your details are used only to respond to your digitization request.',
        back: 'Back',
        next: 'Continue',
        send: 'Send message',
        maxFile: 'Max 10MB',
      },
      labels: {
        name: 'Name *',
        email: 'Email *',
        company: 'Company (optional)',
        projectType: 'Project Type',
        budget: 'Budget Range',
        detailsTitle: 'Tell Us About Your Project',
        detailsSubtitle: 'Help us understand your needs so we can provide the best solution.',
        projectGoals: 'What are your main project goals? *',
        keyFeatures: 'What key features or requirements do you have? *',
        timeline: "What's your desired timeline?",
        challenges: 'What challenges are you trying to solve? (optional)',
        additionalNotes: 'Anything else we should know? (optional)',
        attachment: 'Attach File (Optional)',
      },
      placeholders: {
        name: 'John Doe',
        email: 'john@example.com',
        company: 'Your Company',
        projectGoals: 'e.g., Launch MVP in 3 months, modernize existing system, enter new market...',
        keyFeatures: 'e.g., User authentication, payment processing, mobile app, AI integration...',
        challenges: 'e.g., Slow performance, outdated tech stack, scaling issues...',
        additionalNotes: 'Any other details, constraints, or questions...',
      },
      attachment: {
        chooseFile: 'Choose a file to attach',
        removeFile: 'Remove file',
      },
      actions: {
        send: 'Send Message',
        sending: 'Sending...',
      },
      messages: {
        success: "Thank you! We'll get back to you within 24 hours.",
      },
      options: {
        projectType: [
          { value: '', label: 'Select a type' },
          { value: 'ai-product', label: 'AI Product Development' },
          { value: 'full-stack', label: 'Full-Stack Development' },
          { value: 'design', label: 'Product Design' },
          { value: 'mvp', label: 'MVP Development' },
          { value: 'other', label: 'Other' },
        ],
        budget: [
          { value: '', label: 'Select a range' },
          { value: 'under-25k', label: 'Under $25,000' },
          { value: '25k-50k', label: '$25,000 - $50,000' },
          { value: '50k-100k', label: '$50,000 - $100,000' },
          { value: '100k-plus', label: '$100,000+' },
        ],
        timeline: [
          { value: '', label: 'Select timeline' },
          { value: 'asap', label: 'ASAP (less than 1 month)' },
          { value: '1-3-months', label: '1-3 months' },
          { value: '3-6-months', label: '3-6 months' },
          { value: '6-plus-months', label: '6+ months' },
          { value: 'flexible', label: 'Flexible' },
        ],
      },
      errors: {
        nameRequired: 'Name is required',
        emailRequired: 'Email is required',
        emailInvalid: 'Please enter a valid email',
        projectGoalsRequired: 'Please describe your project goals',
        keyFeaturesRequired: 'Please describe key features or requirements',
        minDetail: 'Please provide more detail (at least 20 characters)',
        attachmentSize: 'File size must be less than 10MB',
        invalidResponse: 'Server returned an invalid response. Please try again later.',
        generic: 'Something went wrong while sending your message. Please try again later or email us directly.',
        failedToSend: 'Failed to send message. Please try again.',
      },
    },
    fr: {
      title: 'Numérisons ce qui compte le plus',
      subtitle:
        'Dites-nous ce que vous voulez numériser (une idée, un processus, un service ou vos opérations) et nous vous aiderons à en faire une solution numérique pratique.',
      responseTime: 'Délai de réponse',
      responseTimeMessage: 'Nous répondons généralement sous 24 heures les jours ouvrables',
      wizard: {
        step1Title: 'Que voulez-vous numériser ?',
        step1Subtitle: 'Commencez par vos coordonnées pour une réponse rapide.',
        step2Title: 'Quel domaine numériser en priorité ?',
        step2Subtitle: 'Choisissez l’option la plus proche de votre besoin.',
        step3Title: 'Quelle fourchette de budget convient ?',
        step3Subtitle: 'Cela nous aide à proposer une approche pratique et abordable.',
        step4Title: 'Parfait, précisons l’essentiel',
        step4Subtitle: 'Partagez vos objectifs et vos exigences principales.',
        step5Title: 'Derniers détails',
        step5Subtitle: 'Délai, contexte, fichier optionnel, puis envoi.',
        reviewTitle: 'Récapitulatif',
        privacyNote: 'Vos informations servent uniquement à répondre à votre demande de numérisation.',
        back: 'Retour',
        next: 'Continuer',
        send: 'Envoyer',
        maxFile: '10 Mo max',
      },
      labels: {
        name: 'Nom *',
        email: 'Courriel *',
        company: 'Entreprise (optionnel)',
        projectType: 'Type de projet',
        budget: 'Fourchette de budget',
        detailsTitle: 'Parlez-nous de votre projet',
        detailsSubtitle: 'Aidez-nous à comprendre vos besoins pour proposer la meilleure solution.',
        projectGoals: 'Quels sont vos principaux objectifs projet ? *',
        keyFeatures: 'Quelles fonctionnalités ou exigences sont importantes ? *',
        timeline: 'Quel est votre calendrier souhaité ?',
        challenges: 'Quels problèmes cherchez-vous à résoudre ? (optionnel)',
        additionalNotes: 'Autre chose à nous signaler ? (optionnel)',
        attachment: 'Ajouter un fichier (optionnel)',
      },
      placeholders: {
        name: 'Jean Dupont',
        email: 'jean@exemple.com',
        company: 'Votre entreprise',
        projectGoals: 'ex. Lancer un MVP en 3 mois, moderniser un système existant, entrer sur un nouveau marché...',
        keyFeatures: 'ex. Authentification, paiements, application mobile, intégration IA...',
        challenges: 'ex. Performances lentes, stack vieillissante, problèmes de passage à l’échelle...',
        additionalNotes: 'Autres détails, contraintes ou questions...',
      },
      attachment: {
        chooseFile: 'Choisir un fichier à joindre',
        removeFile: 'Retirer le fichier',
      },
      actions: {
        send: 'Envoyer le message',
        sending: 'Envoi...',
      },
      messages: {
        success: 'Merci. Nous reviendrons vers vous sous 24 heures.',
      },
      options: {
        projectType: [
          { value: '', label: 'Choisir un type' },
          { value: 'ai-product', label: 'Développement de produits IA' },
          { value: 'full-stack', label: 'Développement full-stack' },
          { value: 'design', label: 'Design produit' },
          { value: 'mvp', label: 'Développement MVP' },
          { value: 'other', label: 'Autre' },
        ],
        budget: [
          { value: '', label: 'Choisir une fourchette' },
          { value: 'under-25k', label: 'Moins de 25 000 $' },
          { value: '25k-50k', label: '25 000 $ - 50 000 $' },
          { value: '50k-100k', label: '50 000 $ - 100 000 $' },
          { value: '100k-plus', label: '100 000 $+' },
        ],
        timeline: [
          { value: '', label: 'Choisir un délai' },
          { value: 'asap', label: 'Dès que possible (moins de 1 mois)' },
          { value: '1-3-months', label: '1-3 mois' },
          { value: '3-6-months', label: '3-6 mois' },
          { value: '6-plus-months', label: '6+ mois' },
          { value: 'flexible', label: 'Flexible' },
        ],
      },
      errors: {
        nameRequired: 'Le nom est requis',
        emailRequired: 'Le courriel est requis',
        emailInvalid: 'Veuillez saisir un courriel valide',
        projectGoalsRequired: 'Veuillez décrire vos objectifs projet',
        keyFeaturesRequired: 'Veuillez décrire les fonctionnalités ou exigences',
        minDetail: 'Veuillez donner plus de détails (au moins 20 caractères)',
        attachmentSize: 'La taille du fichier doit être inférieure à 10 Mo',
        invalidResponse: 'Le serveur a renvoyé une réponse invalide. Veuillez réessayer plus tard.',
        generic: 'Une erreur est survenue pendant l’envoi. Veuillez réessayer plus tard ou nous écrire directement.',
        failedToSend: 'Échec de l’envoi du message. Veuillez réessayer.',
      },
    },
  } as const;

  const t = copy[language];
  const w = t.wizard;

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [step]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = t.errors.nameRequired;
    }

    if (!formData.email.trim()) {
      newErrors.email = t.errors.emailRequired;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t.errors.emailInvalid;
    }

    if (!formData.projectGoals.trim()) {
      newErrors.projectGoals = t.errors.projectGoalsRequired;
    } else if (formData.projectGoals.trim().length < 20) {
      newErrors.projectGoals = t.errors.minDetail;
    }

    if (!formData.keyFeatures.trim()) {
      newErrors.keyFeatures = t.errors.keyFeaturesRequired;
    } else if (formData.keyFeatures.trim().length < 20) {
      newErrors.keyFeatures = t.errors.minDetail;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep = (currentStep: number): boolean => {
    if (currentStep === 1) {
      const e: FormErrors = {};
      if (!formData.name.trim()) e.name = t.errors.nameRequired;
      if (!formData.email.trim()) {
        e.email = t.errors.emailRequired;
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        e.email = t.errors.emailInvalid;
      }
      setErrors(e);
      return Object.keys(e).length === 0;
    }
    if (currentStep === 4) {
      const e: FormErrors = {};
      if (!formData.projectGoals.trim()) {
        e.projectGoals = t.errors.projectGoalsRequired;
      } else if (formData.projectGoals.trim().length < 20) {
        e.projectGoals = t.errors.minDetail;
      }
      if (!formData.keyFeatures.trim()) {
        e.keyFeatures = t.errors.keyFeaturesRequired;
      } else if (formData.keyFeatures.trim().length < 20) {
        e.keyFeatures = t.errors.minDetail;
      }
      setErrors(e);
      return Object.keys(e).length === 0;
    }
    setErrors({});
    return true;
  };

  const goNext = () => {
    if (!isHydrated) return;
    if (!validateStep(step)) return;
    if (step < TOTAL_STEPS) setStep((s) => s + 1);
  };

  const goBack = () => {
    setSubmitError(null);
    if (step > 1) setStep((s) => s - 1);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!isHydrated) return;
    const file = e.target.files?.[0] || null;

    if (file) {
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        setErrors((prev) => ({ ...prev, attachment: t.errors.attachmentSize }));
        setSelectedFile(null);
        setFileName('');
        e.target.value = '';
        return;
      }

      setSelectedFile(file);
      setFileName(file.name);
      if (errors.attachment) {
        setErrors((prev) => ({ ...prev, attachment: undefined }));
      }
    } else {
      setSelectedFile(null);
      setFileName('');
    }
  };

  const resetFormState = () => {
    setFormData({
      name: '',
      email: '',
      company: '',
      projectType: '',
      budget: '',
      projectGoals: '',
      keyFeatures: '',
      timeline: '',
      challenges: '',
      additionalNotes: '',
    });
    setSelectedFile(null);
    setFileName('');
    setStep(1);
    setErrors({});
    const fileInput = document.getElementById('attachment') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  };

  const submitContact = async () => {
    if (!isHydrated || !validateForm()) {
      if (!formData.name.trim() || !formData.email.trim()) setStep(1);
      else if (!formData.projectGoals.trim() || !formData.keyFeatures.trim()) setStep(4);
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const structuredMessage = `
PROJECT GOALS:
${formData.projectGoals}

KEY FEATURES/REQUIREMENTS:
${formData.keyFeatures}

${formData.timeline ? `TIMELINE: ${formData.timeline}\n\n` : ''}${formData.challenges ? `CHALLENGES:\n${formData.challenges}\n\n` : ''}${formData.additionalNotes ? `ADDITIONAL NOTES:\n${formData.additionalNotes}` : ''}`.trim();

      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('company', formData.company);
      formDataToSend.append('projectType', formData.projectType);
      formDataToSend.append('budget', formData.budget);
      formDataToSend.append('message', structuredMessage);

      if (selectedFile) {
        formDataToSend.append('attachment', selectedFile);
      }

      const response = await fetch('/api/contact', {
        method: 'POST',
        body: formDataToSend,
      });

      const contentType = response.headers.get('content-type');
      let data: { error?: string } | undefined;

      if (contentType && contentType.includes('application/json')) {
        try {
          data = await response.json();
        } catch {
          throw new Error(t.errors.invalidResponse);
        }
      } else {
        await response.text();
        throw new Error(t.errors.generic);
      }

      if (!response.ok) {
        throw new Error(data?.error || t.errors.failedToSend);
      }

      setIsSubmitting(false);
      setSubmitSuccess(true);
      resetFormState();

      setTimeout(() => {
        setSubmitSuccess(false);
      }, 6000);
    } catch (error: unknown) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Contact form error:', error);
      }
      setIsSubmitting(false);
      const errorMessage =
        error instanceof Error ? error.message : t.errors.generic;
      setSubmitError(errorMessage);
      setTimeout(() => setSubmitError(null), 8000);
    }
  };

  const onFormSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (step < TOTAL_STEPS) {
      goNext();
      return;
    }
    void submitContact();
  };

  const handleChange = (field: keyof FormData, value: string) => {
    if (!isHydrated) return;
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const projectTypeChoices = t.options.projectType.filter((o) => o.value !== '');
  const budgetChoices = t.options.budget.filter((o) => o.value !== '');

  const labelFor = (options: ReadonlyArray<{ value: string; label: string }>, value: string) => {
    const found = options.find((o) => o.value === value);
    return found?.label ?? (value ? value : '');
  };

  return (
    <section id="contact" className="pt-28 pb-12 sm:pt-32 sm:pb-16 lg:py-24 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="text-center mb-12 sm:mb-14 lg:mb-16">
          <h2 className="font-display text-section-h2 mb-4 sm:mb-5 lg:mb-4 text-gradient-neon">
            {t.title}
          </h2>
          <p className="font-sans text-supporting-lg font-medium text-muted-foreground max-w-2xl mx-auto">
            {t.subtitle}
          </p>
        </div>

        <div className="max-w-lg mx-auto">
          <div className="hidden lg:flex items-center justify-center gap-3 mb-6 pb-4 border-b border-border">
            <div className="p-2 bg-primary/10 rounded-lg flex-shrink-0">
              <Icon name="ClockIcon" size={20} className="text-primary" />
            </div>
            <div className="text-center">
              <span className="font-sans text-caption font-medium text-foreground">{t.responseTime}: </span>
              <span className="text-sm text-muted-foreground">{t.responseTimeMessage}</span>
            </div>
          </div>

          <div className="lg:hidden mb-6">
            <div className="bg-card border border-border rounded-xl p-5 sm:p-6">
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="p-2.5 sm:p-3 bg-primary/10 rounded-lg flex-shrink-0">
                  <Icon name="ClockIcon" size={24} className="text-primary" />
                </div>
                <div>
                  <h3 className="font-sans text-card-title text-foreground mb-2">{t.responseTime}</h3>
                  <p className="text-sm sm:text-base text-muted-foreground">{t.responseTimeMessage}</p>
                </div>
              </div>
            </div>
          </div>

          {submitSuccess ? (
            <div
              ref={cardRef}
              className="rounded-2xl border border-success/40 bg-card/80 backdrop-blur-sm p-8 sm:p-10 text-center shadow-neon-cyan/20 animate-fade-in"
            >
              <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-success/20 ring-2 ring-success/50">
                <Icon name="CheckCircleIcon" size={40} className="text-success" />
              </div>
              <p className="font-sans text-heading-4 text-foreground mb-2">{t.messages.success}</p>
              <p className="text-sm text-muted-foreground">{t.subtitle}</p>
            </div>
          ) : (
            <div
              ref={cardRef}
              className="rounded-2xl border border-border bg-card/90 backdrop-blur-sm p-6 sm:p-8 shadow-[0_8px_40px_rgba(0,255,255,0.06)]"
            >
              <form onSubmit={onFormSubmit} className="space-y-6">
                <div className="flex items-start justify-between gap-3 min-h-[2rem]">
                  {step > 1 ? (
                    <button
                      type="button"
                      onClick={goBack}
                      className="inline-flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-primary transition-colors -mt-0.5"
                      aria-label={w.back}
                    >
                      <span className="text-lg leading-none" aria-hidden>
                        &laquo;
                      </span>
                      <span>{w.back}</span>
                    </button>
                  ) : (
                    <span />
                  )}
                  <span className="text-xs font-medium text-muted-foreground tabular-nums pt-0.5">
                    {step}/{TOTAL_STEPS}
                  </span>
                </div>

                {step === 1 && (
                  <div className="space-y-5 animate-fade-in">
                    <div>
                      <h3 className="font-sans text-card-title text-foreground mb-1">{w.step1Title}</h3>
                      {w.step1Subtitle ? (
                        <p className="text-sm text-muted-foreground">{w.step1Subtitle}</p>
                      ) : null}
                    </div>
                    <div>
                      <label htmlFor="email" className="block font-sans text-caption font-medium text-foreground mb-2">
                        {t.labels.email}
                      </label>
                      <input
                        type="email"
                        id="email"
                        autoComplete="email"
                        value={formData.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                        className={`w-full px-4 py-3.5 text-base bg-background border rounded-lg ${
                          errors.email ? 'border-error' : 'border-border'
                        } text-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20`}
                        placeholder={t.placeholders.email}
                      />
                      {errors.email && (
                        <p className="mt-2 text-sm text-error flex items-center gap-1.5">
                          <Icon name="ExclamationCircleIcon" size={16} />
                          {errors.email}
                        </p>
                      )}
                    </div>
                    <div>
                      <label htmlFor="name" className="block font-sans text-caption font-medium text-foreground mb-2">
                        {t.labels.name}
                      </label>
                      <input
                        type="text"
                        id="name"
                        autoComplete="name"
                        value={formData.name}
                        onChange={(e) => handleChange('name', e.target.value)}
                        className={`w-full px-4 py-3.5 text-base bg-background border rounded-lg ${
                          errors.name ? 'border-error' : 'border-border'
                        } text-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20`}
                        placeholder={t.placeholders.name}
                      />
                      {errors.name && (
                        <p className="mt-2 text-sm text-error flex items-center gap-1.5">
                          <Icon name="ExclamationCircleIcon" size={16} />
                          {errors.name}
                        </p>
                      )}
                    </div>
                    <div>
                      <label htmlFor="company" className="block font-sans text-caption font-medium text-foreground mb-2">
                        {t.labels.company}
                      </label>
                      <input
                        type="text"
                        id="company"
                        autoComplete="organization"
                        value={formData.company}
                        onChange={(e) => handleChange('company', e.target.value)}
                        className="w-full px-4 py-3.5 text-base bg-background border border-border rounded-lg text-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                        placeholder={t.placeholders.company}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">{w.privacyNote}</p>
                  </div>
                )}

                {step === 2 && (
                  <div className="space-y-4 animate-fade-in">
                    <div>
                      <h3 className="font-sans text-card-title text-foreground mb-1">{w.step2Title}</h3>
                      <p className="text-sm text-muted-foreground">{w.step2Subtitle}</p>
                    </div>
                    <div className="flex flex-col gap-2">
                      {projectTypeChoices.map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => handleChange('projectType', opt.value)}
                          className={`w-full rounded-xl border px-4 py-4 text-left text-nav font-medium transition-all duration-200 ${
                            formData.projectType === opt.value
                              ? 'border-primary bg-primary/10 text-primary shadow-neon-cyan/30'
                              : 'border-border bg-background/50 text-foreground hover:border-primary/40'
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {step === 3 && (
                  <div className="space-y-4 animate-fade-in">
                    <div>
                      <h3 className="font-sans text-card-title text-foreground mb-1">{w.step3Title}</h3>
                      <p className="text-sm text-muted-foreground">{w.step3Subtitle}</p>
                    </div>
                    <div className="flex flex-col gap-2">
                      {budgetChoices.map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => handleChange('budget', opt.value)}
                          className={`w-full rounded-xl border px-4 py-4 text-left text-nav font-medium transition-all duration-200 ${
                            formData.budget === opt.value
                              ? 'border-primary bg-primary/10 text-primary shadow-neon-cyan/30'
                              : 'border-border bg-background/50 text-foreground hover:border-primary/40'
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {step === 4 && (
                  <div className="space-y-5 animate-fade-in">
                    <div>
                      <h3 className="font-sans text-card-title text-foreground mb-1">{w.step4Title}</h3>
                      <p className="text-sm text-muted-foreground">{w.step4Subtitle}</p>
                    </div>
                    <div>
                      <label htmlFor="projectGoals" className="block font-sans text-caption font-medium text-foreground mb-2">
                        {t.labels.projectGoals}
                      </label>
                      <textarea
                        id="projectGoals"
                        value={formData.projectGoals}
                        onChange={(e) => handleChange('projectGoals', e.target.value)}
                        rows={3}
                        className={`w-full px-4 py-3 text-base bg-background border rounded-lg ${
                          errors.projectGoals ? 'border-error' : 'border-border'
                        } text-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 resize-none`}
                        placeholder={t.placeholders.projectGoals}
                      />
                      {errors.projectGoals && (
                        <p className="mt-2 text-sm text-error flex items-center gap-1.5">
                          <Icon name="ExclamationCircleIcon" size={16} />
                          {errors.projectGoals}
                        </p>
                      )}
                    </div>
                    <div>
                      <label htmlFor="keyFeatures" className="block font-sans text-caption font-medium text-foreground mb-2">
                        {t.labels.keyFeatures}
                      </label>
                      <textarea
                        id="keyFeatures"
                        value={formData.keyFeatures}
                        onChange={(e) => handleChange('keyFeatures', e.target.value)}
                        rows={3}
                        className={`w-full px-4 py-3 text-base bg-background border rounded-lg ${
                          errors.keyFeatures ? 'border-error' : 'border-border'
                        } text-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 resize-none`}
                        placeholder={t.placeholders.keyFeatures}
                      />
                      {errors.keyFeatures && (
                        <p className="mt-2 text-sm text-error flex items-center gap-1.5">
                          <Icon name="ExclamationCircleIcon" size={16} />
                          {errors.keyFeatures}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {step === 5 && (
                  <div className="space-y-5 animate-fade-in">
                    <div>
                      <h3 className="font-sans text-card-title text-foreground mb-1">{w.step5Title}</h3>
                      <p className="text-sm text-muted-foreground">{w.step5Subtitle}</p>
                    </div>

                    <div>
                      <label htmlFor="timeline" className="block font-sans text-caption font-medium text-foreground mb-2">
                        {t.labels.timeline}
                      </label>
                      <select
                        id="timeline"
                        value={formData.timeline}
                        onChange={(e) => handleChange('timeline', e.target.value)}
                        className="w-full px-4 py-3 text-base bg-background border border-border rounded-lg text-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 appearance-none cursor-pointer"
                      >
                        {t.options.timeline.map((option) => (
                          <option key={`${option.value}-${option.label}`} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label htmlFor="challenges" className="block font-sans text-caption font-medium text-foreground mb-2">
                        {t.labels.challenges}
                      </label>
                      <textarea
                        id="challenges"
                        value={formData.challenges}
                        onChange={(e) => handleChange('challenges', e.target.value)}
                        rows={2}
                        className="w-full px-4 py-3 text-base bg-background border border-border rounded-lg text-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 resize-none"
                        placeholder={t.placeholders.challenges}
                      />
                    </div>

                    <div>
                      <label htmlFor="additionalNotes" className="block font-sans text-caption font-medium text-foreground mb-2">
                        {t.labels.additionalNotes}
                      </label>
                      <textarea
                        id="additionalNotes"
                        value={formData.additionalNotes}
                        onChange={(e) => handleChange('additionalNotes', e.target.value)}
                        rows={2}
                        className="w-full px-4 py-3 text-base bg-background border border-border rounded-lg text-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 resize-none"
                        placeholder={t.placeholders.additionalNotes}
                      />
                    </div>

                    <div>
                      <label htmlFor="attachment" className="block font-sans text-caption font-medium text-foreground mb-2">
                        {t.labels.attachment}
                      </label>
                      <input
                        type="file"
                        id="attachment"
                        onChange={handleFileChange}
                        className="hidden"
                        accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.zip,.rar"
                      />
                      <label
                        htmlFor="attachment"
                        className={`flex items-center justify-between w-full px-4 py-3 bg-background border rounded-lg cursor-pointer transition-all ${
                          errors.attachment ? 'border-error' : 'border-border hover:border-primary/50'
                        }`}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <Icon name="PaperClipIcon" size={20} className="text-primary flex-shrink-0" />
                          <span className="text-foreground text-sm truncate">
                            {fileName || t.attachment.chooseFile}
                          </span>
                        </div>
                        <span className="text-xs text-muted-foreground flex-shrink-0 ml-2">{w.maxFile}</span>
                      </label>
                      {fileName && (
                        <div className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedFile(null);
                              setFileName('');
                              const el = document.getElementById('attachment') as HTMLInputElement;
                              if (el) el.value = '';
                            }}
                            className="text-error text-xs hover:underline"
                          >
                            {t.attachment.removeFile}
                          </button>
                        </div>
                      )}
                      {errors.attachment && (
                        <p className="mt-2 text-sm text-error">{errors.attachment}</p>
                      )}
                    </div>

                    <div className="rounded-xl border border-border/80 bg-background/40 p-4 space-y-2 text-sm">
                      <p className="font-medium text-foreground">{w.reviewTitle}</p>
                      <p className="text-muted-foreground">
                        <span className="text-foreground/80">{formData.name}</span> · {formData.email}
                      </p>
                      {formData.company ? (
                        <p className="text-muted-foreground">
                          {formData.company}
                        </p>
                      ) : null}
                      <p className="text-muted-foreground">
                        {labelFor(t.options.projectType, formData.projectType) || '-'} ·{' '}
                        {labelFor(t.options.budget, formData.budget) || '-'}
                      </p>
                    </div>
                  </div>
                )}

                {submitError && (
                  <div className="bg-error/10 border border-error rounded-lg p-4 flex items-start gap-3">
                    <Icon name="ExclamationCircleIcon" size={22} className="text-error flex-shrink-0 mt-0.5" />
                    <p className="font-sans text-sm font-medium text-error">{submitError}</p>
                  </div>
                )}

                <div className="flex flex-col gap-4 pt-2">
                  <div className="flex justify-center gap-2">
                    {Array.from({ length: TOTAL_STEPS }, (_, i) => (
                      <span
                        key={i}
                        className={`h-2 w-2 rounded-full transition-colors ${
                          i + 1 === step ? 'bg-primary shadow-neon-cyan' : 'bg-muted-foreground/30'
                        }`}
                        aria-hidden
                      />
                    ))}
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-4 rounded-xl bg-primary text-primary-foreground text-nav font-medium hover:shadow-neon-cyan transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:hover:scale-100 flex items-center justify-center gap-2"
                  >
                    {isSubmitting ? (
                      <>
                        <Icon name="ArrowPathIcon" size={20} className="animate-spin" />
                        <span>{t.actions.sending}</span>
                      </>
                    ) : step < TOTAL_STEPS ? (
                      <>
                        <span>{w.next}</span>
                        <Icon name="ChevronRightIcon" size={20} />
                      </>
                    ) : (
                      <>
                        <Icon name="PaperAirplaneIcon" size={20} />
                        <span>{w.send}</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
